import React, { useState, useEffect } from "react";
import axios from "axios";

const CourseContentSummary = ({ platformUrl }) => {
    const [contentSummary, setContentSummary] = useState({});
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const courseUuid = params.get("courseUuid");

    useEffect(() => {
        if (!token || !courseUuid) {
            setError("Missing access token or course UUID. Please try again.");
            setLoading(false);
            return;
        }

        const fetchAllContent = async (parentId = "ROOT") => {
            try {
                const response = await axios.get(
                    `${platformUrl}/learn/api/public/v1/courses/uuid:${courseUuid}/contents?parentId=${parentId}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                const items = response.data.results || [];
                let summary = {};

                // Process current level items
                items.forEach(item => {
                    const type = item.contentHandler?.id || "unknown";
                    summary[type] = (summary[type] || 0) + 1;
                });

                // Recursively fetch nested content
                for (const item of items) {
                    if (item.hasChildren) {
                        const childSummary = await fetchAllContent(item.id);
                        for (const [type, count] of Object.entries(childSummary)) {
                            summary[type] = (summary[type] || 0) + count;
                        }
                    }
                }

                return summary;
            } catch (err) {
                throw new Error(err.response?.data?.message || err.message);
            }
        };

        const fetchData = async () => {
            try {
                const summary = await fetchAllContent();
                setContentSummary(summary);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [platformUrl, courseUuid, token]);

    if (loading) {
        return <div>Loading content summary…</div>;
    }

    if (error) {
        return (
            <div className="bb-container bb-error">
                <h1 className="bb-title">Error</h1>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="bb-container">
            <h1 className="bb-title">Course Content Summary</h1>
            <table className="bb-table">
                <thead>
                <tr>
                    <th>Content Type</th>
                    <th>Count</th>
                </tr>
                </thead>
                <tbody>
                {Object.entries(contentSummary).map(([type, count]) => (
                    <tr key={type}>
                        <td>{type}</td>
                        <td>{count}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

export default CourseContentSummary;
